import { createReducer } from '@reduxjs/toolkit';

import * as schedulerActions from './schedulerActions.js';

const initialState = {
  requesting: false,
  error: null,

  requestingDraft: false,
  errorDraft: null,
  isDraft: false,

  id: 0,
  title: '',
  content: '',
  images: [],
  fb: false,
  tw: false,
  ig: false,
  li: false,
  tg: false,
  pi: false,
  tr: false,
  di: false,
  bs: false,
  wp: false,

  // Multi-account: map of platformId → number[] (account IDs selected for that platform).
  selectedAccountIds: {},
  // Multi-account: map of `${platformId}:${accountId}` → { textBody, files } for per-account overrides.
  perAccountCustoms: {},

  publishResult: null,
};

const customKey = (platformId, accountId) => `${platformId}:${accountId}`;

// Map platform id → legacy boolean field in state. Keeps draft-save compat
// (isFacebook/isInstagram/... flags consumed by backend).
const platformBoolKey = {
  1: 'fb',
  2: 'ig',
  4: 'tw',
  6: 'li',
  7: 'tg',
  9: 'pi',
  10: 'tr',
  11: 'di',
  15: 'bs',
  16: 'wp',
};

const withBoolSync = (state, platformId, hasSelection) => {
  const key = platformBoolKey[platformId];
  if (!key) return state;
  return { ...state, [key]: !!hasSelection };
};

const scheduler = createReducer(initialState, (builder) => {
  builder
    .addCase(schedulerActions.schedulerGetDraftRequest, (state) => ({ ...state, requestingDraft: true, errorDraft: null }))
    .addCase(schedulerActions.schedulerGetDraftError, (state, { payload }) => ({ ...state, errorDraft: payload, requestingDraft: false }))

    .addCase(schedulerActions.schedulerUpdateIsDraft, (state, { payload: isDraft }) => ({ ...state, isDraft }))

    .addCase(schedulerActions.schedulerGetDraftStore, (state, { payload: data }) => {
      console.log('get draft store');

      const {
        title,
        content,
        medias: images,
        isFacebook: fb,
        isTwitter: tw,
        isInstagram: ig,
        isLinkedIn: li,
        isTelegram: tg,
        isPinterest: pi,
        isThreads: tr,
        isDiscord: di,
        isBluesky: bs,
        isWordpress: wp,
      } = data;

      return {
        ...state,

        errorDraft: null,
        requestingDraft: false,

        isDraft: true,
        title,
        content,
        images,
        fb,
        tw,
        ig,
        li,
        tg,
        pi,
        tr,
        di,
        bs,
        wp,
      };
    })

    .addCase(schedulerActions.schedulerUpdatePostStore, (state, { payload: data }) => {
      const {
        title,
        content,
        medias: images,
        isFacebook: fb,
        isTwitter: tw,
        isInstagram: ig,
        isLinkedIn: li,
        isTelegram: tg,
        isPinterest: pi,
        isThreads: tr,
        isDiscord: di,
        isBluesky: bs,
        isWordpress: wp,
      } = data;

      return {
        ...state,

        title,
        content,
        images,
        fb,
        tw,
        ig,
        li,
        tg,
        pi,
        tr,
        di,
        bs,
        wp,
      };
    })

    .addCase(schedulerActions.schedulerUpdateRequest, (state) => ({ ...state, requesting: true, error: null }))
    .addCase(schedulerActions.schedulerUpdateError, (state, { payload }) => ({ ...state, error: payload, requesting: false }))
    .addCase(schedulerActions.schedulerUpdateStore, (state) => ({ ...state, error: false, requesting: false }))

    .addCase(schedulerActions.schedulerUpdateId, (state, { payload: id }) => ({ ...state, id }))
    .addCase(schedulerActions.schedulerUpdateTitle, (state, { payload: title }) => ({ ...state, title }))
    .addCase(schedulerActions.schedulerUpdateContent, (state, { payload: content }) => ({ ...state, content }))
    .addCase(schedulerActions.schedulerUpdateImages, (state, { payload: images }) => ({ ...state, images }))

    .addCase(schedulerActions.schedulerUpdateFB, (state, { payload: fb }) => ({ ...state, fb }))
    .addCase(schedulerActions.schedulerUpdateTW, (state, { payload: tw }) => ({ ...state, tw }))
    .addCase(schedulerActions.schedulerUpdateIG, (state, { payload: ig }) => ({ ...state, ig }))
    .addCase(schedulerActions.schedulerUpdateLI, (state, { payload: li }) => ({ ...state, li }))
    .addCase(schedulerActions.schedulerUpdateTG, (state, { payload: tg }) => ({ ...state, tg }))
    .addCase(schedulerActions.schedulerUpdatePI, (state, { payload: pi }) => ({ ...state, pi }))
    .addCase(schedulerActions.schedulerUpdateTR, (state, { payload: tr }) => ({ ...state, tr }))
    .addCase(schedulerActions.schedulerUpdateDI, (state, { payload: di }) => ({ ...state, di }))

    .addCase(schedulerActions.schedulerUpdatePublishResult, (state, { payload: publishResult }) => ({ ...state, publishResult }))

    // Multi-account: toggle one account for a platform
    .addCase(schedulerActions.schedulerToggleAccount, (state, { payload }) => {
      const { platformId, accountId } = payload;
      const current = state.selectedAccountIds[platformId] || [];
      const nextSelected = current.includes(accountId) ? current.filter((id) => id !== accountId) : [...current, accountId];

      const nextCustoms = { ...state.perAccountCustoms };
      if (current.includes(accountId)) {
        // removed → drop any stored override for this (platform, account)
        delete nextCustoms[customKey(platformId, accountId)];
      }

      const next = {
        ...state,
        selectedAccountIds: { ...state.selectedAccountIds, [platformId]: nextSelected },
        perAccountCustoms: nextCustoms,
      };
      return withBoolSync(next, platformId, nextSelected.length > 0);
    })

    // Multi-account: replace full selection for a platform (e.g. after modal Save)
    .addCase(schedulerActions.schedulerSetSelectedAccounts, (state, { payload }) => {
      const { platformId, accountIds } = payload;
      const nextSelected = { ...state.selectedAccountIds, [platformId]: accountIds };

      // prune orphan per-account customs for this platform
      const prefix = `${platformId}:`;
      const nextCustoms = { ...state.perAccountCustoms };
      Object.keys(nextCustoms).forEach((key) => {
        if (key.startsWith(prefix)) {
          const aid = Number(key.slice(prefix.length));
          if (!accountIds.includes(aid)) delete nextCustoms[key];
        }
      });

      const next = { ...state, selectedAccountIds: nextSelected, perAccountCustoms: nextCustoms };
      return withBoolSync(next, platformId, accountIds.length > 0);
    })

    // Multi-account: initialize override with base content (idempotent)
    .addCase(schedulerActions.schedulerInitAccountCustoms, (state, { payload }) => {
      const { platformId, accountId, baseTextBody, baseFiles } = payload;
      const key = customKey(platformId, accountId);
      if (state.perAccountCustoms[key]) return state;
      return {
        ...state,
        perAccountCustoms: {
          ...state.perAccountCustoms,
          [key]: { textBody: baseTextBody || '', files: baseFiles || [] },
        },
      };
    })

    // Multi-account: set content override for a specific (platform, account)
    .addCase(schedulerActions.schedulerSetAccountContent, (state, { payload }) => {
      const { platformId, accountId, textBody, files } = payload;
      const key = customKey(platformId, accountId);
      const prev = state.perAccountCustoms[key] || { textBody: '', files: [] };
      return {
        ...state,
        perAccountCustoms: {
          ...state.perAccountCustoms,
          [key]: {
            textBody: textBody !== undefined ? textBody : prev.textBody,
            files: files !== undefined ? files : prev.files,
          },
        },
      };
    })

    .addCase(schedulerActions.schedulerClearAllAccounts, (state) => ({
      ...state,
      selectedAccountIds: {},
      perAccountCustoms: {},
      fb: false,
      ig: false,
      tw: false,
      li: false,
      tg: false,
      pi: false,
      tr: false,
      di: false,
      bs: false,
      wp: false,
    }));
});

export default scheduler;
